/**
 * Custom domain hooks — list, add, refresh, remove.
 *
 * Uses direct fetch (with credentials) — the Hono RPC type inference
 * doesn't always trace through nested `.route()` mounts cleanly.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { env } from './env';

export const DOMAINS_KEY = ['admin', 'domains'] as const;

export interface DomainRow {
  host: string;
  status: 'pending' | 'verified' | 'live' | 'failed' | 'suspended';
  verify_token: string;
  cf_custom_hostname_id: string | null;
  verified_at: number | null;
  last_check_at: number | null;
  last_check_error: string | null;
  created_at: number;
}

export interface DomainsResponse {
  cf_configured: boolean;
  domains: DomainRow[];
  cname_target: string;
}

async function adminFetch(path: string, init: RequestInit = {}) {
  return fetch(`${env.apiBaseUrl}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
}

export function useDomains() {
  return useQuery({
    queryKey: DOMAINS_KEY,
    queryFn: async (): Promise<DomainsResponse> => {
      const res = await adminFetch('/api/admin/domains');
      if (!res.ok) throw new Error('failed_to_load_domains');
      return (await res.json()) as DomainsResponse;
    },
    staleTime: 30_000,
    // Auto-poll while any domain is still being verified — bumps to live
    // status without manual refresh once the DNS propagates.
    refetchInterval: (query) => {
      const data = query.state.data as DomainsResponse | undefined;
      const pending = (data?.domains ?? []).some(
        (d) => d.status === 'pending' || d.status === 'verified'
      );
      return pending ? 30_000 : false;
    },
  });
}

export function useAddDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (host: string) => {
      const res = await adminFetch('/api/admin/domains', {
        method: 'POST',
        body: JSON.stringify({ host }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
        throw new Error(err.error ?? 'add_failed');
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: DOMAINS_KEY }),
  });
}

export function useRefreshDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (host: string) => {
      const res = await adminFetch(`/api/admin/domains/${encodeURIComponent(host)}/refresh`, {
        method: 'POST',
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? 'refresh_failed');
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: DOMAINS_KEY }),
  });
}

export function useDeleteDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (host: string) => {
      const res = await adminFetch(`/api/admin/domains/${encodeURIComponent(host)}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('delete_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: DOMAINS_KEY }),
  });
}
